import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { formatCents, formatTimestamp, parseEuroCents } from '../lib/format';
import { PageHelp } from '../components/PageHelp';

interface ReserveYear {
  year: number;
  begin: number;
  result: number;
  cap: number;
  lapsed: number;
  end: number;
  provisional: boolean;
}

interface ReserveDivision {
  divisionId: string;
  name: string;
  openingCents: number | null;
  years: ReserveYear[];
}

interface ReserveResponse {
  startYear: number;
  dataAsOf: string | null;
  divisions: ReserveDivision[];
}

const euro = (cents: number) => formatCents(cents);
const num = { align: 'right' as const, sx: { whiteSpace: 'nowrap' as const } };

/**
 * D-22: Budgetrücklage je Abteilung -- Saldo beim Gesamtverein, positiv = Budgetrücklage,
 * negativ = Verbindlichkeit. Admin und Vorstand sehen alle Abteilungen, Abteilungsleiter nur die
 * eigenen (serverseitig, FR-29). Den Anfangsbestand erfasst der Admin.
 */
export default function BudgetruecklagePage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [data, setData] = useState<ReserveResponse>();
  const [error, setError] = useState<string>();
  const [editing, setEditing] = useState<ReserveDivision>();
  const [amount, setAmount] = useState('');
  const [dialogError, setDialogError] = useState<string>();

  async function load() {
    setData(await api.get<ReserveResponse>('/admin/reserve'));
  }

  useEffect(() => {
    load().catch((err: unknown) =>
      setError(err instanceof Error ? err.message : 'Budgetrücklage konnte nicht geladen werden'),
    );
  }, []);

  async function saveOpening() {
    if (!editing) return;
    const cents = parseEuroCents(amount);
    if (cents === undefined) {
      setDialogError('Bitte einen Betrag wie 15.000,00 eingeben (negativ = Verbindlichkeit).');
      return;
    }
    try {
      await api.put(`/admin/reserve/${encodeURIComponent(editing.divisionId)}`, {
        openingCents: cents,
      });
      setEditing(undefined);
      await load();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    }
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h4">Budgetrücklage</Typography>
        <PageHelp text="Jede Abteilung hat beim Gesamtverein eine Budgetrücklage: Jahresende = Jahresbeginn + Jahresergebnis (netto, alle Buchungen der Abteilung wie im Dashboard). Zum Jahresende ist ein positiver Betrag auf den Durchschnitt der Ausgaben dieses und der beiden Vorjahre begrenzt; der Rest verfällt zugunsten des Gesamtvereins. Ein negativer Betrag ist eine Verbindlichkeit gegenüber dem Gesamtverein, die Überschüsse der Folgejahre tilgen. Das laufende Jahr ist vorläufig. Keine Rücklage im steuerlichen Sinn (§ 62 AO)." />
      </Stack>

      {data && (
        <Typography variant="body2" color="text.secondary">
          Ab 01.01.{data.startYear} · Beträge netto · Datenstand:{' '}
          {data.dataAsOf ? formatTimestamp(data.dataAsOf) : 'noch kein Import'}
        </Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}
      {data && data.divisions.length === 0 && (
        <Typography color="text.secondary">Keine Abteilung zugeordnet.</Typography>
      )}

      {data?.divisions.map((d) => {
        const last = d.years[d.years.length - 1];
        return (
          <TableContainer component={Paper} key={d.divisionId}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ p: 2, pb: 1, justifyContent: 'space-between', alignItems: { sm: 'center' } }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="h6">{d.name}</Typography>
                {last && (
                  <Chip
                    size="small"
                    color={last.end < 0 ? 'warning' : 'default'}
                    label={
                      last.end < 0
                        ? `Verbindlichkeit ${euro(-last.end)}`
                        : `Budgetrücklage ${euro(last.end)}`
                    }
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Anfangsbestand 01.01.{data.startYear}:{' '}
                  {d.openingCents === null ? 'nicht erfasst' : euro(d.openingCents)}
                </Typography>
                {isAdmin && (
                  <Button
                    size="small"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={() => {
                      setDialogError(undefined);
                      setAmount(
                        d.openingCents === null ? '' : euro(d.openingCents).replace(/\s*€$/, ''),
                      );
                      setEditing(d);
                    }}
                  >
                    Ändern
                  </Button>
                )}
              </Stack>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Jahr</TableCell>
                  <TableCell {...num}>Jahresbeginn</TableCell>
                  <TableCell {...num}>Jahresergebnis</TableCell>
                  <TableCell {...num}>Obergrenze</TableCell>
                  <TableCell {...num}>Verfällt</TableCell>
                  <TableCell {...num}>Jahresende</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {d.years.map((y) => (
                  <TableRow key={y.year}>
                    <TableCell>
                      {y.year}
                      {y.provisional && (
                        <Typography variant="caption" color="text.secondary">
                          {' '}
                          (vorläufig)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell {...num}>{euro(y.begin)}</TableCell>
                    <TableCell {...num}>{euro(y.result)}</TableCell>
                    <TableCell {...num}>{euro(y.cap)}</TableCell>
                    <TableCell {...num}>{y.provisional ? '—' : euro(y.lapsed)}</TableCell>
                    <TableCell {...num}>
                      <strong>{euro(y.end)}</strong>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      })}

      <Dialog open={Boolean(editing)} onClose={() => setEditing(undefined)} maxWidth="xs" fullWidth>
        <DialogTitle>Anfangsbestand {editing?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={`Budgetrücklage am 01.01.${data?.startYear ?? ''} (€)`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              helperText="Negativ für eine Verbindlichkeit, z.B. -2.500,00"
              autoFocus
              fullWidth
            />
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(undefined)}>Abbrechen</Button>
          <Button variant="contained" onClick={() => void saveOpening()}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
